import { Link, useParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useMyFarm, useFarmHorse } from '../api/hooks'
import { PageSpinner } from '../components/ui'
import { fmtDate } from '../lib/format'

export function FarmHorseDetail() {
  const { id = '' } = useParams()
  const { t, td, locale } = useI18n()
  const { data: farm, isLoading: farmLoading } = useMyFarm()
  const { data: horse, isLoading } = useFarmHorse(farm?.id, id)

  if (farmLoading || isLoading) return <PageSpinner />
  if (!horse) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-24 text-center">
        <Link to="/farm" className="btn-primary">{t('farm.horse.back')}</Link>
      </main>
    )
  }

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <Link to="/farm" className="text-sm font-medium text-brand-600 dark:text-brand-300 hover:underline">{t('farm.horse.back')}</Link>

      {/* Header */}
      <div className="mt-4 flex items-center gap-5">
        <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-brand-100 dark:bg-brand-800 text-3xl">
          {horse.profilePhotoUrl ? <img src={horse.profilePhotoUrl} alt="" className="h-full w-full object-cover" /> : '🐎'}
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{horse.horseName}</h1>
          {horse.shares.info && (
            <p className="text-brand-600 dark:text-brand-300">
              {[horse.breed, horse.colour, horse.discipline].filter(Boolean).join(' · ')}
            </p>
          )}
          <p className="mt-1 text-sm text-brand-500 dark:text-brand-400">
            {t('farm.horse.owner')}: <span className="font-medium">{horse.ownerName}</span> · <a className="hover:underline" href={`mailto:${horse.ownerEmail}`}>{horse.ownerEmail}</a>
          </p>
          {horse.stableLocation && (
            <p className="mt-1 text-sm text-brand-600 dark:text-brand-300">
              📍 {t('farm.horse.location')}: <span className="font-medium">{horse.stableLocation}</span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {/* Medical */}
        <Section title={t('farm.horse.medical')} hidden={!horse.shares.medical}>
          {horse.medical.length === 0 ? (
            <Empty text={t('farm.horse.noMedical')} />
          ) : (
            <ol className="space-y-4 border-l border-brand-200 dark:border-brand-700 pl-5">
              {horse.medical.map((m) => (
                <li key={m.id} className="relative">
                  <span className="absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full bg-brand-600 ring-4 ring-brand-50 dark:ring-brand-900" />
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-semibold">{m.title || td('medicalCategory', m.category)}</h3>
                    <span className="text-xs text-brand-500">{fmtDate(m.date, locale)}</span>
                  </div>
                  {m.vet && <p className="text-xs text-brand-500">{m.vet}</p>}
                  {m.notes && <p className="text-sm text-brand-700 dark:text-brand-200">{m.notes}</p>}
                </li>
              ))}
            </ol>
          )}
        </Section>

        {/* Reminders */}
        <Section title={t('farm.horse.reminders')} hidden={!horse.shares.reminders}>
          {horse.reminders.length === 0 ? (
            <Empty text={t('farm.horse.noReminders')} />
          ) : (
            <ul className="space-y-2">
              {horse.reminders.map((r) => (
                <li key={r.id} className="flex items-center justify-between rounded-xl border border-brand-100 dark:border-brand-700/60 px-3 py-2 text-sm">
                  <span>{r.title || td('medicalCategory', r.category)}</span>
                  <span className="chip whitespace-nowrap">{t('farm.horse.nextDue')}: {fmtDate(r.nextDue, locale)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Documents */}
        <Section title={t('farm.horse.documents')} hidden={!horse.shares.documents}>
          {horse.documents.length === 0 ? (
            <Empty text={t('farm.horse.noDocuments')} />
          ) : (
            <ul className="divide-y divide-brand-100 dark:divide-brand-700/60">
              {horse.documents.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="truncate">{d.name}</span>
                  {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="shrink-0 font-semibold text-brand-700 dark:text-brand-200 hover:underline">{t('farm.horse.download')}</a>}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </main>
  )
}

function Section({ title, hidden, children }: { title: string; hidden: boolean; children: React.ReactNode }) {
  const { t } = useI18n()
  return (
    <div className="card p-6">
      <h2 className="mb-3 text-lg font-bold">{title}</h2>
      {hidden ? <Empty text={t('farm.horse.hidden')} /> : children}
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-brand-500 dark:text-brand-400">{text}</p>
}
